import axios from 'axios';
import { type FC, type ReactNode, useState } from 'react';
import { useLocation } from 'react-router';

import LandingPage from '@zen/Components/LandingPage';
import { Headline, Icon } from '@zen/DesignSystem';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { parseQueryParams } from '@zen/utils/QueryParams/queryParams';
import type { QueryParams } from '@zen/utils/QueryParams/types';

import ContainerDetailsForm from './ContainerDetailsForm';
import { formatDate } from './helper';
import { containerFormLabels } from './translations';
import type { Container } from './types';

const PublicContainers: FC = () => {
  const { search } = useLocation();
  const queryParams: QueryParams = parseQueryParams(search);
  const { c: containers, trigger, zencargoReference, cutOff } = queryParams;

  const cutOffEndOfDay = new Date(cutOff);

  cutOffEndOfDay.setHours(23, 59, 59, 999);

  const isWithinCutoff = new Date() <= cutOffEndOfDay;

  const [submissionSuccess, setSubmissionSuccess] = useState<boolean>(false);

  const handleSubmit = async (values: Record<string, Container>): Promise<IOkOrErrorResult> => {
    try {
      const response = await axios.post(trigger, {
        containers: Object.values(values),
        zencargoReference
      });

      setSubmissionSuccess(true);

      return response.data;
    } catch (error) {
      return { ok: false, error };
    }
  };

  const renderListAndForm = (): ReactNode => {
    return <ContainerDetailsForm containers={containers} onSubmit={handleSubmit} />;
  };

  const renderSubmissionSuccess = (): ReactNode => {
    return (
      <div className="flex flex-col justify-center items-center h-[55vh] gap-2">
        <div className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-azure-base border-solid">
          <Icon className="text-xl text-azure-base" icon="zicon-tick" />
        </div>

        <Headline level={2}>Submission Complete!</Headline>
        <p className="text-center">{containerFormLabels.submissionSuccess}</p>
      </div>
    );
  };

  const renderCutOffExceeded = (): ReactNode => {
    return (
      <div className="flex flex-col justify-center items-center h-[55vh] gap-2">
        <div className="flex items-center justify-center w-10 h-10 border-solid">
          <Icon className="text-5xl text-red-base" icon="zicon-alert" />
        </div>

        <Headline level={2}>Shipping Instruction Cut-Off Exceeded!</Headline>
        <p className="text-red-base text-center">SI cut-off date was {formatDate(cutOffEndOfDay)}</p>
        <p className="text-center">{containerFormLabels.cutOffExceeded}</p>
      </div>
    );
  };

  return (
    <LandingPage
      data-testid="public-containers-page"
      tagline={null}
      title={!submissionSuccess ? `Help us to get ${zencargoReference} moving / 请帮忙提供 ${zencargoReference} 补料信息` : ''}
      width="full"
    >
      <div className="mb-12 flex items-center justify-center">
        {!isWithinCutoff && renderCutOffExceeded()}
        {!submissionSuccess && isWithinCutoff && renderListAndForm()}
        {submissionSuccess && renderSubmissionSuccess()}
      </div>
    </LandingPage>
  );
};

export default PublicContainers;
